import { useCallback, useEffect, useRef, useState } from 'react';
import { StripeJs } from '@/shared/stripe/StripeJs';
import type {
  SetupIntentResult,
  Stripe,
  StripeBillingDetails,
  StripeElements,
  StripePaymentElement,
} from '@/shared/stripe/stripeTypes';
import { STRIPE_CARD_APPEARANCE, STRIPE_CARD_ELEMENT_OPTIONS } from '../stripeCardElementOptions';

export type StripeElementStatus = 'idle' | 'loading' | 'ready' | 'error';

interface UseStripePaymentElementParams {
  publishableKey: string | undefined;
  clientSecret: string | undefined;
}

const LOAD_ERROR_MESSAGE =
  'The secure card form could not be loaded. Check your connection or any content blocker and try again.';

/**
 * Owns the Stripe.js → Elements → Payment Element lifecycle for one SetupIntent.
 * Render `containerRef` on an always-present <div>; the Element mounts into it once
 * the setup call has produced a client secret and Stripe.js is loaded, and is
 * destroyed on unmount or when the secret changes. `confirm` runs
 * `stripe.confirmSetup` with the dashboard-collected billing details
 * (redirect: 'if_required' — cards never leave the page; 3-D Secure opens in a modal).
 */
export function useStripePaymentElement({ publishableKey, clientSecret }: UseStripePaymentElementParams) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const elementRef = useRef<StripePaymentElement | null>(null);
  const [status, setStatus] = useState<StripeElementStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mountAttempt, setMountAttempt] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!publishableKey || !clientSecret || !container) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setLoadError(null);

    StripeJs.create(publishableKey)
      .then((stripe) => {
        if (cancelled) return;
        const elements = stripe.elements({
          clientSecret,
          appearance: STRIPE_CARD_APPEARANCE,
          loader: 'auto',
        });
        const element = elements.create('payment', STRIPE_CARD_ELEMENT_OPTIONS);
        element.on('ready', () => {
          if (!cancelled) setStatus('ready');
        });
        element.on('loaderror', ({ error }) => {
          if (cancelled) return;
          console.error('Stripe Payment Element failed to load', error);
          setLoadError(LOAD_ERROR_MESSAGE);
          setStatus('error');
        });
        element.mount(container);
        stripeRef.current = stripe;
        elementsRef.current = elements;
        elementRef.current = element;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Script blocked, or Stripe() rejected the key: both are ours to fix, so the
        // customer sees one calm line and the detail goes to the console.
        console.error('Stripe.js failed to load', err);
        setLoadError(LOAD_ERROR_MESSAGE);
        setStatus('error');
      });

    return () => {
      cancelled = true;
      try {
        elementRef.current?.destroy();
      } catch {
        // Already destroyed by Stripe (e.g. after an iframe teardown) — nothing to release.
      }
      elementRef.current = null;
      elementsRef.current = null;
      stripeRef.current = null;
    };
  }, [publishableKey, clientSecret, mountAttempt]);

  const confirm = useCallback(
    async (billingDetails: StripeBillingDetails): Promise<SetupIntentResult> => {
      const stripe = stripeRef.current;
      const elements = elementsRef.current;
      if (!stripe || !elements) {
        return {
          error: { type: 'api_connection_error', message: 'The secure card form is not ready yet.' },
        };
      }
      return stripe.confirmSetup({
        elements,
        confirmParams: { payment_method_data: { billing_details: billingDetails } },
        redirect: 'if_required',
      });
    },
    [],
  );

  /** Re-inject Stripe.js (if it failed) and re-mount the Element on the same intent. */
  const retry = useCallback(() => setMountAttempt((n) => n + 1), []);

  return { containerRef, status, loadError, confirm, retry };
}
